/**
 * Hero Slider: Swiper + background videos (YouTube, Vimeo, HTML5).
 * YouTube slides play through fully before advancing; replay from start when revisited.
 */
import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

let youtubeApiReady = null;

document.addEventListener( 'DOMContentLoaded', () => {
	initHeroSliders();
	initVideos();
} );

function loadYouTubeApi() {
	if ( window.YT && window.YT.Player ) {
		return Promise.resolve();
	}

	if ( ! youtubeApiReady ) {
		youtubeApiReady = new Promise( ( resolve, reject ) => {
			const finish = () => {
				if ( window.YT && window.YT.Player ) {
					resolve();
					return true;
				}
				return false;
			};

			if ( finish() ) {
				return;
			}

			const previousReady = window.onYouTubeIframeAPIReady;

			window.onYouTubeIframeAPIReady = () => {
				if ( typeof previousReady === 'function' ) {
					previousReady();
				}
				finish();
			};

			const existingScript = document.querySelector(
				'script[src*="youtube.com/iframe_api"]'
			);

			if ( ! existingScript ) {
				const tag = document.createElement( 'script' );
				tag.src = 'https://www.youtube.com/iframe_api';
				tag.onerror = reject;
				document.head.appendChild( tag );
			}

			let attempts = 0;
			const poll = window.setInterval( () => {
				attempts += 1;
				if ( finish() ) {
					window.clearInterval( poll );
				} else if ( attempts > 100 ) {
					window.clearInterval( poll );
					reject( new Error( 'YouTube API failed to load.' ) );
				}
			}, 100 );
		} );
	}

	return youtubeApiReady;
}

function initHeroSliders() {
	const swiperEls = document.querySelectorAll( '.hero__swiper' );
	if ( ! swiperEls.length ) {
		return;
	}

	const hasSwiperYoutube = document.querySelector(
		'.hero__swiper .hero__video[data-type="youtube"]'
	);

	const boot = hasSwiperYoutube ? loadYouTubeApi() : Promise.resolve();

	boot.then( () => {
		swiperEls.forEach( ( el ) => initHeroSwiper( el ) );
	} ).catch( () => {
		swiperEls.forEach( ( el ) => initHeroSwiper( el, true ) );
	} );
}

function initHeroSwiper( el, useYoutubeFallback = false ) {
	const hero = el.closest( '.hero' );
	if ( ! hero ) {
		return;
	}

	const next = hero.querySelector( '.hero__arrow--next' );
	const prev = hero.querySelector( '.hero__arrow--prev' );
	const pagination = hero.querySelector( '.hero__pagination' );
	const slides = el.querySelectorAll( '.swiper-slide' );
	const youtubeSlides = new Map();

	slides.forEach( ( slide, index ) => {
		const videoContainer = slide.querySelector(
			'.hero__video[data-type="youtube"]'
		);
		if ( ! videoContainer ) {
			return;
		}

		const videoId = videoContainer.getAttribute( 'data-id' );
		if ( ! videoId ) {
			return;
		}

		youtubeSlides.set( index, {
			container: videoContainer,
			videoId,
			player: null,
			isReady: false,
			shouldPlayWhenReady: false,
			pendingPlay: false,
			hasStartedPlayback: false,
			playFallbackTimer: null,
			endVerifyTimer: null,
		} );
	} );

	let swiper = null;
	let videoProgressTimer = null;
	let advancingFromVideo = false;

	const clearVideoProgress = () => {
		if ( videoProgressTimer ) {
			clearInterval( videoProgressTimer );
			videoProgressTimer = null;
		}
	};

	const clearPlayFallback = ( data ) => {
		if ( data.playFallbackTimer ) {
			clearTimeout( data.playFallbackTimer );
			data.playFallbackTimer = null;
		}
	};

	const clearEndVerify = ( data ) => {
		if ( data.endVerifyTimer ) {
			clearTimeout( data.endVerifyTimer );
			data.endVerifyTimer = null;
		}
	};

	const getYouTubeSlideIndex = ( data ) => {
		for ( const [ slideIndex, slideData ] of youtubeSlides.entries() ) {
			if ( slideData === data ) {
				return slideIndex;
			}
		}
		return -1;
	};

	const verifyVideoEnded = ( data, slideIndex ) => {
		data.endVerifyTimer = null;

		if (
			! swiper ||
			swiper.activeIndex !== slideIndex ||
			! data.player ||
			! data.hasStartedPlayback
		) {
			return;
		}

		const YTState = window.YT.PlayerState;
		const state =
			typeof data.player.getPlayerState === 'function'
				? data.player.getPlayerState()
				: null;

		if ( state !== YTState.ENDED ) {
			return;
		}

		const duration =
			typeof data.player.getDuration === 'function'
				? data.player.getDuration()
				: 0;

		if ( duration <= 1 ) {
			return;
		}

		advanceAfterVideo( slideIndex );
	};

	const handleYouTubeEnded = ( data, slideIndex ) => {
		if (
			! swiper ||
			swiper.activeIndex !== slideIndex ||
			! data.hasStartedPlayback
		) {
			return;
		}

		const duration =
			typeof data.player.getDuration === 'function'
				? data.player.getDuration()
				: 0;
		const current =
			typeof data.player.getCurrentTime === 'function'
				? data.player.getCurrentTime()
				: 0;

		if ( duration <= 1 ) {
			clearEndVerify( data );
			data.endVerifyTimer = setTimeout( () => {
				verifyVideoEnded( data, slideIndex );
			}, 300 );
			return;
		}

		if ( current > 0 && current < duration - 2 ) {
			playYouTubeSlide( data, { force: true } );
			return;
		}

		advanceAfterVideo( slideIndex );
	};

	const updateVideoProgress = ( data, slideIndex ) => {
		clearVideoProgress();
		videoProgressTimer = setInterval( () => {
			const player = data.player;
			if (
				! player ||
				typeof player.getDuration !== 'function' ||
				typeof player.getCurrentTime !== 'function'
			) {
				return;
			}

			const duration = player.getDuration();
			const current = player.getCurrentTime();
			if ( duration > 0 ) {
				const progress = current / duration;
				el.style.setProperty(
					'--progress',
					`${ ( -( 1 - progress ) * 100 ).toFixed( 2 ) }%`
				);

				if (
					swiper &&
					swiper.activeIndex === slideIndex &&
					data.hasStartedPlayback &&
					current >= duration - 0.75
				) {
					clearVideoProgress();
					advanceAfterVideo( slideIndex );
				}
			}
		}, 100 );
	};

	const resumeYouTubeSlide = ( data, slideIndex ) => {
		if ( ! data.pendingPlay || ! data.player ) {
			return;
		}

		data.pendingPlay = false;
		clearPlayFallback( data );

		if ( typeof data.player.playVideo === 'function' ) {
			data.player.playVideo();
		}

		updateVideoProgress( data, slideIndex );
	};

	const playYouTubeSlide = ( data, { force = false } = {} ) => {
		if ( ! data.isReady || ! data.player ) {
			data.shouldPlayWhenReady = true;
			return;
		}

		const slideIndex = getYouTubeSlideIndex( data );
		const YTState = window.YT.PlayerState;
		const playerState =
			typeof data.player.getPlayerState === 'function'
				? data.player.getPlayerState()
				: null;

		if (
			! force &&
			data.hasStartedPlayback &&
			( playerState === YTState.PLAYING ||
				playerState === YTState.BUFFERING )
		) {
			return;
		}

		data.shouldPlayWhenReady = false;
		data.pendingPlay = true;
		data.hasStartedPlayback = false;
		clearPlayFallback( data );
		clearEndVerify( data );

		if ( typeof data.player.mute === 'function' ) {
			data.player.mute();
		}

		if ( typeof data.player.seekTo === 'function' ) {
			data.player.seekTo( 0, true );
			data.playFallbackTimer = setTimeout( () => {
				resumeYouTubeSlide( data, slideIndex );
			}, 400 );
			return;
		}

		resumeYouTubeSlide( data, slideIndex );
	};

	const pauseYouTubeSlide = ( data ) => {
		data.shouldPlayWhenReady = false;
		data.pendingPlay = false;
		data.hasStartedPlayback = false;
		clearPlayFallback( data );
		clearEndVerify( data );

		if ( ! data.isReady || ! data.player ) {
			return;
		}

		if ( typeof data.player.stopVideo === 'function' ) {
			data.player.stopVideo();
		}
	};

	const stopSwiperAutoplay = () => {
		if ( swiper?.autoplay ) {
			swiper.autoplay.stop();
		}
	};

	const startSwiperAutoplay = () => {
		if ( swiper?.autoplay ) {
			swiper.autoplay.start();
		}
	};

	const isYoutubeSlide = ( slideIndex ) => {
		if ( youtubeSlides.has( slideIndex ) ) {
			return true;
		}

		const slide = slides[ slideIndex ];
		if ( ! slide ) {
			return false;
		}

		const videoContainer = slide.querySelector(
			'.hero__video[data-type="youtube"]'
		);
		const videoId = videoContainer?.getAttribute( 'data-id' );

		return Boolean( videoContainer && videoId );
	};

	const handleActiveSlide = () => {
		if ( ! swiper ) {
			return;
		}

		clearVideoProgress();
		stopSwiperAutoplay();

		const activeIndex = swiper.activeIndex;
		const activeYoutube =
			youtubeSlides.get( activeIndex ) ||
			( isYoutubeSlide( activeIndex ) ? { pending: true } : null );

		youtubeSlides.forEach( ( data, slideIndex ) => {
			if ( slideIndex === activeIndex ) {
				playYouTubeSlide( data );
			} else {
				pauseYouTubeSlide( data );
			}
		} );

		if ( ! activeYoutube ) {
			el.style.setProperty( '--progress', '-100%' );
			startSwiperAutoplay();
		}
	};

	const advanceAfterVideo = ( slideIndex ) => {
		if (
			advancingFromVideo ||
			! swiper ||
			swiper.activeIndex !== slideIndex
		) {
			return;
		}

		advancingFromVideo = true;
		clearVideoProgress();

		if ( swiper.isEnd ) {
			advancingFromVideo = false;
			return;
		}

		swiper.slideNext();
		window.setTimeout( () => {
			advancingFromVideo = false;
		}, 500 );
	};

	const bindYoutubePlayer = ( data, slideIndex ) => {
		const playerMount = document.createElement( 'div' );
		playerMount.className = 'hero__video-player';
		data.container.appendChild( playerMount );
		data.container.style.opacity = '0';

		data.player = new window.YT.Player( playerMount, {
			videoId: data.videoId,
			width: '100%',
			height: '100%',
			playerVars: {
				autoplay: 0,
				mute: 1,
				controls: 0,
				rel: 0,
				modestbranding: 1,
				playsinline: 1,
				enablejsapi: 1,
				origin: window.location.origin,
			},
			events: {
				onReady() {
					data.isReady = true;
					data.container.style.opacity = '1';

					if (
						data.shouldPlayWhenReady ||
						( swiper && swiper.activeIndex === slideIndex )
					) {
						playYouTubeSlide( data );
					}
				},
				onStateChange( event ) {
					const YTState = window.YT.PlayerState;

					if ( event.data === YTState.PLAYING ) {
						data.hasStartedPlayback = true;
						data.pendingPlay = false;
						clearPlayFallback( data );
					}

					if (
						data.pendingPlay &&
						( event.data === YTState.CUED ||
							event.data === YTState.PAUSED ||
							event.data === YTState.UNSTARTED )
					) {
						resumeYouTubeSlide( data, slideIndex );
						return;
					}

					if ( event.data !== YTState.ENDED ) {
						return;
					}

					handleYouTubeEnded( data, slideIndex );
				},
			},
		} );
	};

	if ( useYoutubeFallback || ! ( window.YT && window.YT.Player ) ) {
		youtubeSlides.forEach( ( data ) => {
			initYouTube( data.container, data.videoId, false );
			data.container.style.opacity = '1';
		} );
	}

	swiper = new Swiper( el, {
		modules: [ Navigation, Pagination, Autoplay ],
		slidesPerView: 1,
		slidesPerGroup: 1,
		spaceBetween: 0,
		loop: false,
		autoplay:
			youtubeSlides.size > 0
				? {
						delay: 8000,
						disableOnInteraction: false,
						enabled: false,
				  }
				: {
						delay: 8000,
						disableOnInteraction: false,
				  },
		navigation: { nextEl: next, prevEl: prev },
		pagination: { el: pagination, clickable: true },
		on: {
			init( swiperInstance ) {
				el.style.setProperty( '--progress', '-100%' );
				if ( isYoutubeSlide( swiperInstance.activeIndex ) ) {
					stopSwiperAutoplay();
				}
				handleActiveSlide();
			},
			slideChange( swiperInstance ) {
				if ( isYoutubeSlide( swiperInstance.activeIndex ) ) {
					stopSwiperAutoplay();
				}
				handleActiveSlide();
			},
			autoplay( swiperInstance ) {
				if ( isYoutubeSlide( swiperInstance.activeIndex ) ) {
					stopSwiperAutoplay();
				}
			},
			autoplayTimeLeft( swiperInstance, time, progress ) {
				if ( isYoutubeSlide( swiperInstance.activeIndex ) ) {
					stopSwiperAutoplay();
					return;
				}

				el.style.setProperty(
					'--progress',
					`${ ( -progress * 100 ).toFixed( 2 ) }%`
				);
			},
		},
	} );

	if ( ! useYoutubeFallback && window.YT && window.YT.Player ) {
		youtubeSlides.forEach( ( data, slideIndex ) => {
			bindYoutubePlayer( data, slideIndex );
		} );
	}
}

function initVideos() {
	document.querySelectorAll( '.hero__video[data-type]' ).forEach( ( el ) => {
		if ( el.closest( '.hero__swiper' ) ) {
			return;
		}

		const type = el.getAttribute( 'data-type' );
		const id = el.getAttribute( 'data-id' );
		if ( type === 'youtube' && id ) {
			initYouTube( el, id, true );
		} else if ( type === 'vimeo' && id ) {
			initVimeo( el, id );
		} else if ( type === 'html5' ) {
			initHtml5( el );
		}
	} );
}

function initYouTube( container, videoId, loop = true ) {
	if ( container.querySelector( 'iframe' ) ) {
		return;
	}

	container.style.opacity = '0';
	const params = new URLSearchParams( {
		autoplay: '1',
		mute: '1',
		controls: '0',
		rel: '0',
		playsinline: '1',
		enablejsapi: '1',
		origin: window.location.origin,
	} );

	if ( loop ) {
		params.set( 'loop', '1' );
		params.set( 'playlist', videoId );
	}

	const iframe = document.createElement( 'iframe' );
	iframe.src = `https://www.youtube.com/embed/${ videoId }?${ params }`;
	iframe.setAttribute( 'frameborder', '0' );
	iframe.setAttribute(
		'allow',
		'autoplay; encrypted-media; picture-in-picture'
	);
	iframe.setAttribute( 'allowfullscreen', '' );
	iframe.className = 'hero__video-iframe';
	iframe.onload = () => {
		container.style.opacity = '1';
	};
	container.appendChild( iframe );
	setTimeout( () => {
		container.style.opacity = '1';
	}, 2000 );
}

function initVimeo( container, videoId ) {
	container.style.opacity = '0';
	const params = new URLSearchParams( {
		background: 1,
		autoplay: 1,
		muted: 1,
		loop: 1,
	} );
	const iframe = document.createElement( 'iframe' );
	iframe.src = `https://player.vimeo.com/video/${ videoId }?${ params }`;
	iframe.setAttribute( 'frameborder', '0' );
	iframe.setAttribute( 'allow', 'autoplay; fullscreen' );
	iframe.setAttribute( 'allowfullscreen', '' );
	iframe.className = 'hero__video-iframe';
	iframe.onload = () => {
		container.style.opacity = '1';
	};
	container.appendChild( iframe );
	setTimeout( () => {
		container.style.opacity = '1';
	}, 2000 );
}

function initHtml5( container ) {
	container.style.opacity = '0';
	const mp4 = container.getAttribute( 'data-mp4' );
	const webm = container.getAttribute( 'data-webm' );
	const poster = container.getAttribute( 'data-poster' );
	const video = document.createElement( 'video' );
	video.setAttribute( 'playsinline', '' );
	video.setAttribute( 'muted', '' );
	video.setAttribute( 'autoplay', '' );
	video.setAttribute( 'loop', '' );
	if ( poster ) {
		video.setAttribute( 'poster', poster );
	}
	video.className = 'hero__video-html5';
	if ( webm ) {
		const s = document.createElement( 'source' );
		s.src = webm;
		s.type = 'video/webm';
		video.appendChild( s );
	}
	if ( mp4 ) {
		const s = document.createElement( 'source' );
		s.src = mp4;
		s.type = 'video/mp4';
		video.appendChild( s );
	}
	video.addEventListener(
		'canplay',
		() => {
			container.style.opacity = '1';
		},
		{ once: true }
	);
	container.appendChild( video );
	video.play().catch( () => {} );
}
